import type { AutoplayConfig } from "../store.js";
import { safeGetJson, safeSetJson } from "./persist.js";

const STORAGE_KEY = "vb:autoplay-presets";

export interface AutoplayPreset {
  /** Stable id used as the React key and the localStorage entry handle. */
  id: string;
  /** Player-visible name. Editable. */
  name: string;
  /** Full snapshot of the autoplay config at save time. */
  config: AutoplayConfig;
  /** Read-only flag for the three shipped defaults so the UI hides Delete. */
  builtin?: boolean;
}

/**
 * Three sane starting points so first-time users see something useful in the
 * picker. None of them are "winning" strategies — crash is negative-EV, full
 * stop. They illustrate the shape of each strategy with safe defaults.
 */
const BUILTIN_PRESETS: AutoplayPreset[] = [
  {
    id: "builtin-conservative",
    name: "Conservative · 1.5×",
    builtin: true,
    config: {
      strategy: "manual",
      numberOfBets: 20,
      baseBet: 0.0005,
      autoCashoutTarget: 1.5,
      onWinPct: 0,
      onLossPct: 0,
      stopOnProfit: 0.005,
      stopOnLoss: 0.005,
      skipAnimations: true,
    },
  },
  {
    id: "builtin-martingale",
    name: "Martingale · 2×",
    builtin: true,
    config: {
      strategy: "martingale",
      numberOfBets: 10,
      baseBet: 0.0005,
      autoCashoutTarget: 2,
      onWinPct: 0,
      onLossPct: 0,
      stopOnProfit: 0.01,
      stopOnLoss: 0.02,
      skipAnimations: true,
    },
  },
  {
    id: "builtin-paroli",
    name: "Paroli · 3×",
    builtin: true,
    config: {
      strategy: "paroli",
      numberOfBets: 30,
      baseBet: 0.0005,
      autoCashoutTarget: 3,
      onWinPct: 0,
      onLossPct: 0,
      stopOnProfit: 0.01,
      stopOnLoss: 0.005,
      skipAnimations: true,
    },
  },
];

/**
 * Reads user-saved presets from localStorage. Always merges the three built-in
 * starters at the top so the picker is never empty even if nothing has been
 * saved. The localStorage shape is just the user list — built-ins are derived,
 * not stored, so we can ship new starters without migration churn.
 */
export function loadPresets(): AutoplayPreset[] {
  const userPresets = safeGetJson<AutoplayPreset[]>(STORAGE_KEY, []);
  return [...BUILTIN_PRESETS, ...userPresets];
}

export function saveUserPresets(presets: AutoplayPreset[]): void {
  // Strip built-ins before persisting — they're derived.
  const userOnly = presets.filter((p) => !p.builtin);
  safeSetJson(STORAGE_KEY, userOnly);
}

export function addPreset(name: string, config: AutoplayConfig): AutoplayPreset {
  const id = `user-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
  const next: AutoplayPreset = { id, name, config };
  const userOnly = loadPresets().filter((p) => !p.builtin);
  saveUserPresets([...userOnly, next]);
  return next;
}

export function deletePreset(id: string): void {
  const userOnly = loadPresets().filter((p) => !p.builtin && p.id !== id);
  saveUserPresets(userOnly);
}

/** Export presets as a compact JSON string for clipboard share between devices. */
export function exportPresets(): string {
  return JSON.stringify(loadPresets().filter((p) => !p.builtin));
}

/** Import presets from a JSON blob. Replaces the existing user list on success;
 *  silently no-ops on parse failure so a bad clipboard paste can't nuke saved
 *  presets unexpectedly.                                                       */
export function importPresets(json: string): boolean {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return false;
    // Best-effort sanity — entries must at least have id, name, config.
    const valid = parsed.filter(
      (p: unknown): p is AutoplayPreset =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as AutoplayPreset).id === "string" &&
        typeof (p as AutoplayPreset).name === "string" &&
        typeof (p as AutoplayPreset).config === "object",
    );
    saveUserPresets(valid);
    return true;
  } catch {
    return false;
  }
}
